<template>
  <!-- Search -->
  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
    <div>
      <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Search</label>
      <InputText v-model="searchQuery" placeholder="Name or matric no..." class="w-full" />
    </div>
  </div>

  <!-- Table -->
  <div class="bg-white rounded-2xl shadow-sm border border-gray-100">
    <div class="p-6 border-b border-gray-100 flex items-center justify-between">
      <h3 class="text-sm font-semibold text-gray-700">Archived Students</h3>
      <span v-if="archivedData" class="text-xs text-gray-400">{{ archivedData.total }} total</span>
    </div>

    <div v-if="loading" class="flex items-center justify-center py-12">
      <i class="pi pi-spin pi-spinner text-2xl text-primary"></i>
    </div>

    <template v-else-if="archivedData && archivedData.data.length > 0">
      <DataTable :value="archivedData.data" stripedRows size="small" class="p-datatable-sm">
        <Column header="Matric No">
          <template #body="{ data }">
            <span class="text-sm text-gray-700">{{ data.matric_no || '-' }}</span>
          </template>
        </Column>
        <Column header="Name">
          <template #body="{ data }">
            <span class="font-medium text-gray-900">{{ data.surname }} {{ data.first_name }}</span>
          </template>
        </Column>
        <Column header="Gender">
          <template #body="{ data }">
            <span class="text-sm text-gray-700 capitalize">{{ data.gender || '-' }}</span>
          </template>
        </Column>
        <Column header="Reason">
          <template #body="{ data }">
            <div v-if="data.transfer" class="text-sm">
              <Tag severity="info" class="text-xs">Transferred</Tag>
              <span class="text-gray-500 ml-1">to {{ data.transfer.target_school_name }}</span>
              <p v-if="data.transfer.reason" class="text-xs text-gray-400 mt-1">{{ data.transfer.reason }}</p>
            </div>
            <span v-else class="text-sm text-gray-400">Deleted</span>
          </template>
        </Column>
        <Column header="Archived On">
          <template #body="{ data }">
            <span class="text-sm text-gray-500">{{ formatDate(data.deleted_at) }}</span>
          </template>
        </Column>
        <Column header="" style="width: 120px">
          <template #body="{ data }">
            <div class="flex gap-1">
              <Button v-if="!data.transfer" icon="pi pi-replay" severity="success" text rounded size="small" v-tooltip.top="'Restore'" @click="handleRestore(data)" :loading="actionLoading" />
              <span v-else v-tooltip.top="'Transferred students can only be restored by the parent school'" class="inline-flex items-center">
                <i class="pi pi-lock text-gray-300 text-sm"></i>
              </span>
              <Button icon="pi pi-trash" severity="danger" text rounded size="small" v-tooltip.top="'Delete Permanently'" @click="confirmDelete(data)" :loading="actionLoading" />
            </div>
          </template>
        </Column>
      </DataTable>

      <div class="p-4 border-t border-gray-100">
        <Paginator :rows="archivedData.per_page" :totalRecords="archivedData.total" :first="(archivedData.current_page - 1) * archivedData.per_page" @page="onPageChange" />
      </div>
    </template>

    <div v-else class="flex flex-col items-center justify-center py-12 text-gray-400">
      <i class="pi pi-inbox text-3xl mb-3"></i>
      <p class="text-sm">No archived students found.</p>
    </div>
  </div>

  <!-- Confirm Delete Dialog -->
  <Dialog v-model:visible="deleteDialogVisible" header="Permanently Delete Student" modal class="w-full max-w-sm">
    <p class="text-sm text-gray-700">
      Are you sure you want to permanently delete <strong>{{ deleteTarget?.surname }} {{ deleteTarget?.first_name }}</strong>?
      This action cannot be undone.
    </p>
    <template #footer>
      <Button label="Cancel" severity="secondary" @click="deleteDialogVisible = false" />
      <Button label="Delete Permanently" severity="danger" icon="pi pi-trash" :loading="actionLoading" @click="handleDelete" />
    </template>
  </Dialog>

  <!-- Result Dialog -->
  <Dialog v-model:visible="resultDialogVisible" header="Result" modal class="w-full max-w-sm">
    <div class="p-4 rounded-xl" :class="resultData?.success ? 'bg-green-50' : 'bg-red-50'">
      <p :class="resultData?.success ? 'text-green-800' : 'text-red-800'" class="text-sm font-medium">{{ resultData?.message }}</p>
    </div>
    <template #footer>
      <Button label="OK" @click="resultDialogVisible = false" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useQuery, useMutation } from '@vue/apollo-composable';
import { ARCHIVED_STUDENTS, RESTORE_STUDENT, PERMANENTLY_DELETE_STUDENT } from '@/graphql/Queries/archivedStudents';
import type { ArchivedStudentRow } from '@/types/composition-functions';
import { getErrorMessage } from '@/utils/error';

const searchQuery = ref('');
const currentPage = ref(1);
const perPage = ref(20);
const deleteDialogVisible = ref(false);
const resultDialogVisible = ref(false);
const deleteTarget = ref<ArchivedStudentRow | null>(null);
const resultData = ref<{ success: boolean; message: string } | null>(null);
const actionLoading = ref(false);

const queryVars = computed(() => ({
  search: searchQuery.value.trim() || undefined,
  page: currentPage.value,
  per_page: perPage.value,
}));

const { result: archivedResult, loading, refetch } = useQuery(ARCHIVED_STUDENTS, queryVars, { fetchPolicy: 'network-only' });
const archivedData = computed(() => archivedResult.value?.archivedStudents ?? null);

const { mutate: restoreMutation } = useMutation(RESTORE_STUDENT);
const { mutate: deleteMutation } = useMutation(PERMANENTLY_DELETE_STUDENT);

const formatDate = (dateStr: string) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const onPageChange = (event: { page: number }) => {
  currentPage.value = event.page + 1;
};

let searchTimeout: ReturnType<typeof setTimeout> | null = null;
watch(searchQuery, () => {
  if (searchTimeout) clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => { currentPage.value = 1; }, 400);
});

const handleRestore = async(student: ArchivedStudentRow) => {
  actionLoading.value = true;
  try {
    const res = await restoreMutation({ student_id: student.student_id });
    resultData.value = res?.data?.restoreStudent ?? { success: false, message: 'Failed' };
    resultDialogVisible.value = true;
    if (resultData.value?.success) refetch();
  } catch (e) {
    resultData.value = { success: false, message: getErrorMessage(e) };
    resultDialogVisible.value = true;
  } finally {
    actionLoading.value = false;
  }
};

const confirmDelete = (student: ArchivedStudentRow) => {
  deleteTarget.value = student;
  deleteDialogVisible.value = true;
};

const handleDelete = async() => {
  if (!deleteTarget.value) return;
  actionLoading.value = true;
  try {
    const res = await deleteMutation({ student_id: deleteTarget.value.student_id });
    resultData.value = res?.data?.permanentlyDeleteStudent ?? { success: false, message: 'Failed' };
    deleteDialogVisible.value = false;
    resultDialogVisible.value = true;
    if (resultData.value?.success) refetch();
  } catch (e) {
    resultData.value = { success: false, message: getErrorMessage(e) };
    deleteDialogVisible.value = false;
    resultDialogVisible.value = true;
  } finally {
    actionLoading.value = false;
  }
};
</script>
